import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Save, CheckCircle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Typography from "@tiptap/extension-typography";
import Focus from "@tiptap/extension-focus";
import { Markdown } from "tiptap-markdown";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Link } from "@tiptap/extension-link";
import { Highlight } from "@tiptap/extension-highlight";
import { Underline } from "@tiptap/extension-underline";
import { TextAlign } from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import FloatingActionBar from "./components/FloatingActionBar";
import RecordingBar from "./components/RecordingBar";
import EnhancedRecordingBar from "./components/EnhancedRecordingBar";
import SlidingSidebar from "./components/SlidingSidebar";
import OnlineStatusIndicator from "../shared/components/OnlineStatusIndicator";
import { DocumentService } from "../shared/services/DocumentService";
import { useAuthPrompt } from "../shared/contexts/AuthPromptContext";
import AuthPromptModal from "../shared/components/AuthPromptModal";
import { BillingModal } from "../Billing/components/BillingModal";
import ConfettiExplosion from "react-confetti-explosion";
import { dictationService } from "../shared/services/DictationService";
import { useRemainingCredits } from "../shared/providers/RemainingCreditsProvider";
import { UpgradePrompt } from "./components/UpgradePrompt";
import supabase from "../shared/supabase/supabase";
import { useMicrophonePermission } from "../shared/hooks/useMicrophonePermission";
import SignInPill from "../shared/components/SignInPill";
import { useAnalytics } from "../shared/hooks/useAnalytics";
import "./styles/mobile-optimizations.css";

const EditorPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "edit";
  const initialId = searchParams.get("id") || null;
  const initialTitle = searchParams.get("title") || "Untitled Document";
  const initialContent = searchParams.get("content") || "";

  const [title, setTitle] = useState(initialTitle);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<
    "rewrite" | "translate" | null
  >(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isRecording, setIsRecording] = useState(false); // Will be set to true after starting recording
  const [isWaitingForPermission, setIsWaitingForPermission] = useState(false); // Waiting for mic permission
  const [recordingText, setRecordingText] = useState("");
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showAuthPromptForMax, setShowAuthPromptForMax] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [upgradedPlan, setUpgradedPlan] = useState<string>("");
  const [isProcessingRewrite, setIsProcessingRewrite] = useState(false);
  const [documentId, setDocumentId] = useState<string | null>(initialId);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Custom history: [rawTranscript, editedV1, editedV2, ...] like mobile
  const MAX_EDIT_VERSIONS = 10;
  const historyRef = useRef<string[]>([]);
  const historyIndexRef = useRef(-1);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [historyLength, setHistoryLength] = useState(0);
  const [rawTranscript, setRawTranscript] = useState<string | null>(null);
  const isInitializingRef = useRef(!!initialId);
  const historyDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const isUndoRedoRef = useRef(false);

  // Real-time dictation states
  const [finalText, setFinalText] = useState("");
  const [interimText, setInterimText] = useState("");
  const [enhancedText, setEnhancedText] = useState("");
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [useRealRecording, setUseRealRecording] = useState(true); // Toggle for real vs mock
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const hasStartedRecording = useRef(false); // Prevent multiple starts
  const recordingStartTimeRef = useRef<number | null>(null); // Track recording start time
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null); // Timer for recording duration limit
  const MAX_RECORDING_DURATION_PREMIUM = 2 * 60 * 60 * 1000; // 2 hours for premium users
  const MAX_RECORDING_DURATION_FREE = 1 * 60 * 60 * 1000; // 1 hour for free/offline users
  const originalTranscriptionRef = useRef<string | null>(null); // Track original transcription before enhancement
  const backendCreatedDocRef = useRef(false); // Track if backend created the document from recording
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    // Load saved language preference from localStorage
    return localStorage.getItem("dictation_language") || "en";
  }); // Language selection
  const languageRef = useRef(selectedLanguage); // Ref to avoid re-render issues

  const {
    usage,
    limits,
    isLoading: creditsLoading,
    refreshCredits,
    incrementOfflineWords,
  } = useRemainingCredits();
  const micPermission = useMicrophonePermission();
  const analytics = useAnalytics();

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3, 4, 5, 6],
            HTMLAttributes: {
              class: "font-semibold",
            },
          },
          bulletList: {
            keepMarks: true,
            keepAttributes: false,
          },
          orderedList: {
            keepMarks: true,
            keepAttributes: false,
          },
        }),
        Markdown.configure({
          html: true,
          breaks: true,
          transformCopiedText: true,
          transformPastedText: true,
          linkify: true,
          tightLists: true,
        }),
        Placeholder.configure({
          placeholder: "Start writing your document...",
          emptyEditorClass: "is-editor-empty",
        }),
        Typography,
        Focus.configure({
          className: "has-focus",
          mode: "all",
        }),
        Table.configure({
          resizable: true,
          HTMLAttributes: {
            class: "border-collapse table-fixed",
          },
        }),
        TableRow,
        TableHeader.configure({
          HTMLAttributes: {
            class: "border border-gray-300 bg-gray-100 px-4 py-2 font-semibold",
          },
        }),
        TableCell.configure({
          HTMLAttributes: {
            class: "border border-gray-300 px-4 py-2",
          },
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            class: "text-sky-500 underline hover:text-sky-600 cursor-pointer",
          },
        }),
        Highlight.configure({
          multicolor: true,
          HTMLAttributes: {
            class: "bg-yellow-200",
          },
        }),
        Underline,
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        Color,
        TextStyle,
      ],
      content: initialContent,
      editorProps: {
        attributes: {
          class:
            "prose prose-base sm:prose-lg prose-slate max-w-none focus:outline-none prose-headings:font-semibold prose-h1:text-2xl sm:prose-h1:text-3xl md:prose-h1:text-4xl prose-h2:text-xl sm:prose-h2:text-2xl md:prose-h2:text-3xl prose-h3:text-lg sm:prose-h3:text-xl md:prose-h3:text-2xl prose-h4:text-base sm:prose-h4:text-lg md:prose-h4:text-xl prose-h5:text-sm sm:prose-h5:text-base md:prose-h5:text-lg prose-h6:text-xs sm:prose-h6:text-sm md:prose-h6:text-base prose-p:leading-6 sm:prose-p:leading-7 prose-ul:list-disc prose-ol:list-decimal prose-li:my-1 prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs sm:prose-code:text-sm prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:overflow-x-auto prose-pre:text-xs sm:prose-pre:text-sm prose-table:w-full prose-table:overflow-x-auto prose-img:rounded-lg prose-img:shadow-lg prose-img:max-w-full",
        },
      },
      onUpdate: ({ editor }) => {
        // Skip during initialization or undo/redo
        if (isInitializingRef.current) return;
        if (isUndoRedoRef.current) {
          isUndoRedoRef.current = false;
          return;
        }

        // Save to custom history (debounced)
        if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
        historyDebounceRef.current = setTimeout(() => {
          const md = (editor?.storage as any)?.markdown?.getMarkdown?.() || editor.getText();
          const idx = historyIndexRef.current;
          if (idx >= 0 && historyRef.current[idx] === md) return;
          // Truncate any redo states
          if (idx < historyRef.current.length - 1) {
            historyRef.current = historyRef.current.slice(0, idx + 1);
          }
          historyRef.current.push(md);
          // Enforce limit: keep transcript (index 0) + max edits
          if (historyRef.current.length > MAX_EDIT_VERSIONS + 1) {
            historyRef.current.splice(1, 1);
          }
          historyIndexRef.current = historyRef.current.length - 1;
          setHistoryIndex(historyIndexRef.current);
          setHistoryLength(historyRef.current.length);
        }, 300);

        // Auto-save with debounce
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(async () => {
          // Get markdown content to preserve formatting
          const content = (editor?.storage as any)?.markdown?.getMarkdown?.() || editor.getText();

          const contentVersionIdx = historyIndexRef.current;

          if (documentId) {
            // Update existing document
            await DocumentService.updateDocument(documentId, {
              title,
              editedText: content,
              contentVersionIdx,
            });
            setLastSaved(new Date());
            return;
          }

          // No documentId - check if we should create
          // Skip if backend already created the document (from recording)
          if (backendCreatedDocRef.current) {
            return;
          }

          // Create document only if content exists
          if (!content.trim()) {
            return;
          }

          // Calculate duration if this was from a recording
          let duration = 0;
          if (recordingStartTimeRef.current) {
            duration = Math.floor(
              (Date.now() - recordingStartTimeRef.current) / 1000
            );
          }

          // Create new document (localStorage for unauthenticated, Supabase for authenticated)
          const doc = await DocumentService.saveDocument({
            title,
            originalText: originalTranscriptionRef.current || null,
            editedText: content,
            duration,
          });
          setDocumentId("id" in doc ? doc.id : doc.localId);

          // Reset refs after saving
          recordingStartTimeRef.current = null;
          originalTranscriptionRef.current = null;
          setLastSaved(new Date());
        }, 1000); // Save after 1 second of inactivity
      },
    },
    []
  );

  // Fetch full document (including transcript) and seed TipTap history
  const docIdForHistory = initialId || documentId;
  useEffect(() => {
    if (!editor || !docIdForHistory) return;
    // Skip if history already seeded (e.g. from initial load)
    if (historyRef.current.length > 0) return;

    const fetchAndSeedHistory = async () => {
      try {
        const doc = await DocumentService.getDocument(docIdForHistory);
        if (!doc) {
          isInitializingRef.current = false;
          return;
        }

        const transcript = doc.transcript as Array<{ text: string; start_time: number; end_time: number }> | null;
        if (!transcript || transcript.length === 0) {
          // No transcript — just init history with current content
          const content = doc.edited_text || "";
          if (content) {
            historyRef.current = [content];
            historyIndexRef.current = 0;
            setHistoryIndex(0);
            setHistoryLength(1);
          }
          isInitializingRef.current = false;
          return;
        }

        // Build raw transcript string with timestamps
        const rawText = transcript
          .map((s) => {
            const totalSeconds = Math.floor(s.start_time);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            const timestamp = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
            return `[${timestamp}] ${s.text}`;
          })
          .join("\n\n");

        setRawTranscript(rawText);

        // Initialize history: [rawTranscript, editedContent]
        const editedContent = doc.edited_text || "";
        historyRef.current = [rawText, editedContent];
        // Respect saved index, cap to max 1 on reload
        const idx = Math.min(doc.content_version_idx ?? 1, 1);
        console.log(`[LOAD] content_version_idx from DB=${doc.content_version_idx}, capped idx=${idx}`);
        historyIndexRef.current = idx;
        setHistoryIndex(idx);
        setHistoryLength(2);

        // Set editor to the correct version
        isUndoRedoRef.current = true;
        editor.commands.setContent(historyRef.current[idx]);

        isInitializingRef.current = false;
      } catch (error) {
        console.error("Error fetching document for history seeding:", error);
        isInitializingRef.current = false;
      }
    };

    fetchAndSeedHistory();
  }, [editor, docIdForHistory]);



  // Auto-save when title changes
  const previousTitleRef = useRef(title);
  useEffect(() => {
    // Only trigger if title actually changed (not on mount or other re-renders)
    if (previousTitleRef.current === title) return;
    previousTitleRef.current = title;

    // Don't save if editor not ready or during initialization
    if (!editor) return;
    if (isInitializingRef.current) return;

    // Clear any existing save timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Save after a short delay
    saveTimeoutRef.current = setTimeout(async () => {
      // Get markdown content to preserve formatting
      const content = (editor?.storage as any)?.markdown?.getMarkdown?.() || editor.getText();

      if (documentId) {
        // Update existing document with new title
        await DocumentService.updateDocument(documentId, {
          title,
          editedText: content,
        });
        setLastSaved(new Date());
        return;
      }

      // No documentId - check if we should create
      // Skip if backend already created the document (from recording)
      if (backendCreatedDocRef.current) {
        return;
      }

      // Create document only if title is meaningful
      if (!title.trim() || title === "Untitled Document") {
        return;
      }

      // Create new document (localStorage for unauthenticated, Supabase for authenticated)
      let duration = 0;
      if (recordingStartTimeRef.current) {
        duration = Math.floor(
          (Date.now() - recordingStartTimeRef.current) / 1000
        );
      }

      const doc = await DocumentService.saveDocument({
        title,
        originalText: originalTranscriptionRef.current || null,
        editedText: content || "",
        duration,
      });
      setDocumentId("id" in doc ? doc.id : doc.localId);
      recordingStartTimeRef.current = null;
      originalTranscriptionRef.current = null;
      setLastSaved(new Date());
    }, 1000); // Save after 1 second of inactivity

    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [title, documentId, editor]); // Need all deps to avoid stale closures

  // Store credits in refs to avoid re-render issues
  const creditsRef = useRef({
    isUnlimited: usage.isUnlimited,
    remainingWords: usage.remainingWords,
  });
  useEffect(() => {
    creditsRef.current = {
      isUnlimited: usage.isUnlimited,
      remainingWords: usage.remainingWords,
    };
  }, [usage.isUnlimited, usage.remainingWords]);

  // Update editor content when recording text changes (after recording stops)
  useEffect(() => {
    if (!isRecording && recordingText && editor && !editor.isDestroyed) {
      editor.commands.setContent(recordingText);
      try {
        editor.commands.focus('end');
      } catch (err) {
        // Silently ignore focus errors
      }
    }
  }, [isRecording, recordingText, editor]);

  // Start real-time recording when component mounts in record mode
  useEffect(() => {
    // Only run once on mount when in record mode
    if (mode === "record" && useRealRecording && !hasStartedRecording.current) {
      hasStartedRecording.current = true;

      // Check if user has credits first - using ref values
      const userHasCredits =
        creditsRef.current.isUnlimited || creditsRef.current.remainingWords > 0;

      if (!userHasCredits) {
        // No credits - stay in edit mode and show upgrade prompt
        setIsRecording(false);
        setShowUpgradePrompt(true);
        return;
      }

      // Show recording bar immediately
      setIsRecording(true);

      // Check and request microphone permission first
      const initRecording = async () => {
        // Wait for initial permission check to complete
        let attempts = 0;
        while (micPermission.isChecking && attempts < 20) {
          await new Promise((resolve) => setTimeout(resolve, 50));
          attempts++;
        }

        if (micPermission.hasPermission) {
          startRecording();
        } else if (
          !micPermission.hasPermission &&
          (micPermission.needsPermission || micPermission.status === "prompt")
        ) {
          const granted = await micPermission.requestPermission();
          if (granted) {
            startRecording();
          } else {
            setIsRecording(false);
            setRecordingError(
              "Microphone permission is required for voice recording."
            );
          }
        } else if (micPermission.isBlocked) {
          setIsRecording(false);
          setRecordingError(
            "Microphone access is blocked. Please enable it in your browser settings."
          );
          micPermission.openSettings();
        } else {
          // Fallback: if status is still 'checking' or unknown, try requesting anyway
          const granted = await micPermission.requestPermission();
          if (granted) {
            startRecording();
          } else {
            setIsRecording(false);
          }
        }
      };
      initRecording();
    }

    // Cleanup on unmount
    return () => {
      hasStartedRecording.current = false;
      // Clear recording duration timer
      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      if (isRecording && useRealRecording) {
        stopRecording(false);
      }
      dictationService.disconnect();
    };
  }, [mode, useRealRecording]); // Only re-run if mode or useRealRecording changes, not on permission changes

  // Simulate streaming text when recording (for mock mode)
  useEffect(() => {
    if (isRecording && !useRealRecording) {
      const messages = [
        "Today we're going to discuss ",
        "the implementation of our new ",
        "voice recording feature. ",
        "This feature will allow users ",
        "to capture their thoughts ",
        "quickly and efficiently. ",
        "The system uses advanced ",
        "speech recognition technology ",
        "to provide real-time transcription ",
        "with high accuracy. ",
        "We've designed the interface ",
        "to be intuitive and responsive, ",
        "ensuring a seamless user experience. ",
        "The recording quality is optimized ",
        "for various environments, ",
        "from quiet offices to busy cafes. ",
        "Our AI-powered enhancement ",
        "automatically improves clarity ",
        "and removes background noise. ",
        "Users can easily edit ",
        "and format their transcriptions ",
        "using our built-in editor. ",
        "The system supports multiple languages ",
        "and can detect language switches ",
        "automatically during recording. ",
        "All recordings are securely stored ",
        "and can be accessed from any device. ",
        "We're excited about the possibilities ",
        "this feature opens up for productivity ",
        "and creative expression. ",
      ];

      let currentIndex = 0;
      const interval = setInterval(() => {
        if (currentIndex < messages.length) {
          setRecordingText((prev) => prev + messages[currentIndex]);
          currentIndex++;
        } else {
          clearInterval(interval);
        }
      }, 300); // Faster streaming for more realistic effect

      return () => clearInterval(interval);
    }
  }, [isRecording]);

  const handleBack = () => {
    navigate("/dashboard");
  };

  // Visualize audio levels
  const visualizeAudio = useCallback(() => {
    if (!analyserRef.current || !isRecording) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const animate = () => {
      if (!analyserRef.current || !isRecording) return;

      analyserRef.current.getByteTimeDomainData(dataArray);

      // Calculate RMS for volume
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / bufferLength);
      const level = Math.min(1, rms * 25);
      setAudioLevel(level);

      if (isRecording) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animate();
  }, [isRecording]);

  const startRecording = async () => {
    try {
      // Prevent multiple simultaneous recordings
      if (dictationService.isConnectedToBackend()) {
        return;
      }

      // Track recording start time
      recordingStartTimeRef.current = Date.now();

      // Check if user has credits first - use ref to get latest values
      const hasCredits =
        creditsRef.current.isUnlimited || creditsRef.current.remainingWords > 0;
      if (!hasCredits) {
        analytics.webappUserHitTheirLimits();
        setShowUpgradePrompt(true);
        setIsRecording(false); // Stay in edit mode when no credits
        recordingStartTimeRef.current = null; // Reset start time
        return;
      }

      // Clear all states for fresh start
      setRecordingError(null);
      setFinalText("");
      setInterimText("");
      setEnhancedText("");
      setRecordingText("");
      setIsEnhancing(false);

      // Get auth token
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      // Connect to backend WebSocket
      await dictationService.connect(
        {
          token,
          language: languageRef.current, // Use language from ref to avoid re-render issues
        },
        {
          onPartialTranscript: (text) => {
            setInterimText(text);
            // Don't update editor content during streaming
          },
          onFinalTranscript: (text) => {
            setFinalText((prev) => (prev ? prev + " " + text : text));
            setInterimText("");
            // Don't update editor content during streaming
          },
          onEnhancementChunk: (chunk) => {
            setEnhancedText((prev) => prev + chunk);
          },
          onEnhancementComplete: async (enhanced, original) => {
            // For authenticated users, fetch the Document created by backend FIRST to prevent duplicate
            const isAuth = await DocumentService.isAuthenticated();
            if (isAuth) {
              try {
                // Fetch latest Document (backend just created it)
                const documents = await DocumentService.getDocuments();
                if (documents.length > 0) {
                  const latestDoc = documents[0]; // Documents are sorted by created_at desc
                  const docId = 'id' in latestDoc ? latestDoc.id : latestDoc.localId;
                  setDocumentId(docId);
                  // Mark that backend created this document - skip auto-save insert
                  backendCreatedDocRef.current = true;
                  // Clear refs since Document already saved on backend
                  recordingStartTimeRef.current = null;
                  originalTranscriptionRef.current = null;
                }
              } catch (error) {
                console.error("Error fetching backend-created Document:", error);
              }
            } else {
              // For unauthenticated users, store enhanced text as original
              originalTranscriptionRef.current = enhanced;
            }

            // NOW set recording text (this will trigger editor update & auto-save)
            // If authenticated: documentId is already set → auto-save will UPDATE
            // If not authenticated: originalTranscriptionRef is set → auto-save will CREATE with enhanced text
            setRecordingText(enhanced);

            // Stop microphone and disconnect service
            try {
              dictationService.disconnect();

              // Stop audio visualization
              if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
              }

              // Clear recording timer
              if (recordingTimerRef.current) {
                clearTimeout(recordingTimerRef.current);
                recordingTimerRef.current = null;
              }
            } catch (err) {
              console.error("Error disconnecting dictation service:", err);
            }

            // FINALLY close the recording bar (this triggers re-render)
            setIsEnhancing(false);
            setIsRecording(false);

            // Handle usage tracking asynchronously (don't wait)
            if (enhanced) {
              const words = enhanced
                .split(/\s+/)
                .filter((w) => w.length > 0).length;

              // Track enhancement analytics
              if (original) {
                const originalWords = original
                  .split(/\s+/)
                  .filter((w) => w.length > 0).length;
                analytics.webappDictationEnhanced({
                  originalLength: originalWords,
                  enhancedLength: words,
                  language: languageRef.current,
                });
              }

              // Track dictation stopped
              const duration = recordingStartTimeRef.current
                ? Math.floor(
                    (Date.now() - recordingStartTimeRef.current) / 1000
                  )
                : 0;
              analytics.webappUserStoppedDictation({
                wordsSpoken: words,
                durationSeconds: duration,
                enhanced: true,
                language: languageRef.current,
                cancelled: false,
              });

              // Fire and forget - don't await these operations
              (async () => {
                const {
                  data: { user },
                } = await supabase.auth.getUser();
                if (!user && incrementOfflineWords) {
                  incrementOfflineWords(words);
                }
                // For online users, the backend handles the increment
                await refreshCredits();
              })();
            }
          },
          onError: (error) => {
            setRecordingError(error);
          },
          onNoCredits: () => {
            analytics.webappUserHitTheirLimits();
            setShowUpgradePrompt(true);
            setIsRecording(false);
          },
        }
      );

      // Start microphone recording with selected device
      await dictationService.startRecording(
        micPermission.deviceId || undefined
      );

      // Track analytics
      const currentDevice = micPermission.devices?.find(
        (d) => d.deviceId === micPermission.deviceId
      );
      analytics.webappUserStartedDictation(
        languageRef.current,
        currentDevice?.label || "Default Microphone"
      );

      // Set up audio visualization
      const audioContext = new AudioContext();
      analyserRef.current = audioContext.createAnalyser();
      analyserRef.current.fftSize = 2048;

      // Start visualization
      visualizeAudio();

      // Set up maximum recording timer based on user status
      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
      }

      // Determine max duration based on user status
      const isOffline = !token; // No token means offline/unauthenticated
      const isFreePlan = !creditsRef.current.isUnlimited; // Not unlimited means free plan
      const maxDuration =
        isOffline || isFreePlan
          ? MAX_RECORDING_DURATION_FREE
          : MAX_RECORDING_DURATION_PREMIUM;
      const durationHours = maxDuration / (60 * 60 * 1000);

      recordingTimerRef.current = setTimeout(() => {
        setRecordingError(
          `Maximum recording duration of ${durationHours} hour${
            durationHours > 1 ? "s" : ""
          } reached`
        );
        stopRecording(true);
      }, maxDuration);
    } catch (error) {
      console.error("Failed to start recording:", error);
      setRecordingError(
        "Failed to access microphone. Please check permissions."
      );
      setIsRecording(false);
      // Clear timer if start failed
      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  const stopRecording = async (enhance: boolean = true) => {
    try {
      // Clear recording duration timer
      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }

      // Stop audio visualization
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      if (enhance) {
        // With whisper, text arrives after stop - always go to enhancing state
        setIsEnhancing(true);
        setEnhancedText("");
      }

      // Stop recording and optionally enhance
      await dictationService.stopRecording(enhance);

      if (!enhance) {
        setIsRecording(false);
        // Transfer any existing text to editor when stopping without enhancement
        const textToTransfer = finalText || recordingText;
        if (editor && textToTransfer) {
          editor.commands.setContent(textToTransfer);
        }
      }
    } catch (error) {
      console.error("Failed to stop recording:", error);
      setIsRecording(false);
    }
  };

  const handleStopRecording = () => {
    if (useRealRecording) {
      stopRecording(true);
    } else {
      // Mock mode
      setIsRecording(false);
      // Transfer recording text to editor
      if (editor && recordingText) {
        editor.commands.setContent(recordingText);
      }
    }
  };

  const handleTitleClick = () => {
    setIsEditingTitle(true);
  };

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    if (!title.trim()) {
      setTitle("Untitled Document");
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleTitleBlur();
    }
  };

  const handleActionClick = (action: "copy" | "rewrite" | "translate") => {
    if (action === "copy") {
      // Handle copy with tooltip
      const text = editor?.getText() || "";
      navigator.clipboard.writeText(text);
    } else {
      setSidebarMode(action);
      setIsSidebarOpen(true);
    }
  };

  const handleUpgradeSuccess = (planName: string) => {
    // Close billing modal
    setShowBillingModal(false);

    // Show celebration after a brief delay
    setTimeout(() => {
      setUpgradedPlan(planName);
      setShowConfetti(true);
      setShowSuccessMessage(true);

      // Hide success message after a few seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 4000);

      // Reset confetti after animation
      setTimeout(() => {
        setShowConfetti(false);
      }, 3500);
    }, 300);
  };

  const handleRewrite = async (option: string) => {
    setIsProcessingRewrite(true);

    try {
      // Get markdown content from the editor using the storage.getMarkdown() method
      const currentMarkdown =
        (editor?.storage as any)?.markdown?.getMarkdown?.() || "";
      const currentText = editor?.getText() || "";

      // Skip if no text
      if (!currentText.trim()) {
        setIsProcessingRewrite(false);
        return;
      }

      // Track analytics for rewrite or translate
      if (option.startsWith("translate_")) {
        // Extract language name from translate_spanish -> spanish
        const targetLanguage = option.replace("translate_", "");
        analytics.webappUserTranslatedText({
          targetLanguage,
          textLength: currentText.length,
        });
      } else {
        // It's a rewrite option
        const rewriteType = option.startsWith("custom:") ? "custom" : option;
        analytics.webappUserUsedRewrite({
          rewriteType,
          textLength: currentText.length,
        });
      }

      // Call the editing API with markdown content
      const response = await fetch(
        `${
          process.env.REACT_APP_EXPRESS_BACKEND_URL || "https://api.writevoice.io"
        }/api/edit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text: currentMarkdown, // Send markdown instead of HTML
            feature: option.startsWith("translate_") ? option : option,
            customInstruction: option.startsWith("custom:")
              ? option.substring(7)
              : undefined,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to process request");
      }

      const data = await response.json();

      if (data.editedText) {
        // Update the editor content with the AI response (markdown)
        editor?.commands.setContent(data.editedText);
      }
    } catch (error) {
      console.error("Error calling editing API:", error);
      // Show error toast or notification here if you have one
    } finally {
      setIsProcessingRewrite(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <OnlineStatusIndicator />
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
        }}
        className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-gray-100 z-40"
        style={{
          boxShadow: "0 1px 3px -1px rgba(0, 0, 0, 0.04)",
        }}
      >
        <div className="px-3 sm:px-6 py-2 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:flex-1 min-w-0">
            {/* Back Button */}
            <motion.button
              whileHover={{ x: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleBack}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              <ArrowLeft
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600"
                strokeWidth={1.5}
              />
            </motion.button>

            {/* Document Title */}
            <div className="flex items-center flex-1 min-w-0 max-w-[calc(100%-3rem)] sm:max-w-lg">
              {isEditingTitle ? (
                <motion.input
                  initial={{ scale: 0.98 }}
                  animate={{ scale: 1 }}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={handleTitleBlur}
                  onKeyDown={handleTitleKeyDown}
                  autoFocus
                  className="text-sm sm:text-lg font-medium text-gray-900 bg-transparent border-b border-sky-500 outline-none px-1 w-full"
                />
              ) : (
                <motion.h1
                  whileHover={{ scale: 1.02 }}
                  onClick={handleTitleClick}
                  className="text-sm sm:text-lg font-medium text-gray-900 cursor-pointer hover:text-gray-700 transition-colors truncate w-full"
                  title={title}
                >
                  {title}
                </motion.h1>
              )}

            </div>
          </div>

          {/* Right side - Badge, Sign-in pill and Save Status */}
          <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto justify-center sm:justify-start">
            {/* Raw/Edited Badge */}
            {rawTranscript && (
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  historyIndex === 0
                    ? "bg-green-100 text-green-600"
                    : "bg-orange-100 text-orange-600"
                }`}
              >
                {historyIndex === 0 ? "Raw" : "Edited"}
              </span>
            )}
            <SignInPill />

            {/* Save Status */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center space-x-2 text-xs text-gray-400"
            >
              {lastSaved && (
                <>
                  <Save className="w-3.5 h-3.5" strokeWidth={2} />
                  <span className="hidden md:inline">
                    Auto-saved {lastSaved.toLocaleTimeString()}
                  </span>
                  <span className="md:hidden">Saved</span>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Main Editor Container */}
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          delay: 0.1,
          duration: 0.5,
        }}
        className="pt-32 sm:pt-24 pb-24 sm:pb-32 px-4 sm:px-6 md:px-8"
      >
        <div className="max-w-4xl mx-auto">
          {/* Editor Title in Document */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.2,
              type: "spring",
              stiffness: 260,
              damping: 20,
            }}
          >
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled Document"
              className="w-full text-4xl font-bold text-gray-900 placeholder:text-gray-300 
                       bg-transparent border-none outline-none mb-8 leading-tight"
            />
          </motion.div>

          {/* Tiptap Editor */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.3,
              type: "spring",
              stiffness: 260,
              damping: 20,
            }}
            className="prose-container relative w-full max-w-5xl mx-auto px-4"
          >
            {isProcessingRewrite && (
              <div className="absolute inset-0 bg-white/95 z-10 rounded-lg">
                <div className="h-full flex flex-col justify-start pt-8 space-y-4 px-4">
                  {/* Skeleton loading lines */}
                  <motion.div
                    className="flex flex-col space-y-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    {[...Array(12)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="flex space-x-2"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                      >
                        {/* Simulate varying line lengths */}
                        <motion.div
                          className="h-5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"
                          style={{ width: `${Math.random() * 40 + 60}%` }}
                          animate={{
                            backgroundPosition: [
                              "0% 50%",
                              "100% 50%",
                              "0% 50%",
                            ],
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                        />
                        {Math.random() > 0.7 && (
                          <motion.div
                            className="h-5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"
                            style={{ width: `${Math.random() * 20 + 10}%` }}
                            animate={{
                              backgroundPosition: [
                                "0% 50%",
                                "100% 50%",
                                "0% 50%",
                              ],
                            }}
                            transition={{
                              duration: 1.5,
                              repeat: Infinity,
                              ease: "linear",
                              delay: 0.2,
                            }}
                          />
                        )}
                      </motion.div>
                    ))}
                  </motion.div>

                  {/* Processing text */}
                  <motion.div
                    className="absolute bottom-8 left-0 right-0 text-center"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-lg">
                      <div className="flex space-x-1">
                        <motion.span
                          className="w-2 h-2 bg-sky-500 rounded-full"
                          animate={{ scale: [1, 1.5, 1] }}
                          transition={{
                            duration: 0.6,
                            repeat: Infinity,
                            delay: 0,
                          }}
                        />
                        <motion.span
                          className="w-2 h-2 bg-sky-500 rounded-full"
                          animate={{ scale: [1, 1.5, 1] }}
                          transition={{
                            duration: 0.6,
                            repeat: Infinity,
                            delay: 0.2,
                          }}
                        />
                        <motion.span
                          className="w-2 h-2 bg-sky-500 rounded-full"
                          animate={{ scale: [1, 1.5, 1] }}
                          transition={{
                            duration: 0.6,
                            repeat: Infinity,
                            delay: 0.4,
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        AI is rewriting your text
                      </span>
                    </div>
                  </motion.div>
                </div>
              </div>
            )}
            <EditorContent
              editor={editor}
              className={`min-h-[500px] prose prose-gray prose-lg max-w-none
                       prose-headings:font-medium prose-headings:text-gray-900
                       prose-p:text-gray-700 prose-p:leading-relaxed
                       prose-strong:text-gray-900 prose-strong:font-semibold
                       prose-em:text-gray-700
                       prose-blockquote:text-gray-600 prose-blockquote:border-l-gray-300
                       prose-code:text-sky-600 prose-code:bg-sky-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                       prose-pre:bg-gray-900 prose-pre:text-gray-100
                       prose-ul:text-gray-700 prose-ol:text-gray-700
                       prose-li:marker:text-gray-400
                       focus:outline-none
                       ${isProcessingRewrite ? "opacity-0" : "opacity-100"}
                       transition-opacity duration-300`}
            />
          </motion.div>
        </div>
      </motion.main>

      {/* Floating Action Bar or Recording Bar */}
      {isRecording ? (
        useRealRecording ? (
          <EnhancedRecordingBar
            finalText={finalText}
            interimText={interimText}
            enhancedText={enhancedText}
            isEnhancing={isEnhancing}
            error={recordingError}
            audioLevel={audioLevel}
            onStop={handleStopRecording}
            showUpgradePrompt={!(usage.isUnlimited || usage.remainingWords > 0)}
            remainingWords={usage.remainingWords}
            onUpgrade={() => {
              setShowBillingModal(true);
              setIsRecording(false);
            }}
            micPermission={micPermission}
            selectedLanguage={selectedLanguage}
            onLanguageChange={async (lang) => {
              // Track analytics
              if (lang !== selectedLanguage) {
                analytics.webappLanguageChanged({
                  oldLanguage: selectedLanguage,
                  newLanguage: lang,
                });
              }

              // Store language choice immediately
              localStorage.setItem("dictation_language", lang);
              setSelectedLanguage(lang);
              languageRef.current = lang;

              // If currently recording, restart with new language
              if (isRecording && useRealRecording) {
                // Stop audio visualization
                if (animationFrameRef.current) {
                  cancelAnimationFrame(animationFrameRef.current);
                  animationFrameRef.current = null;
                }

                // Clear all transcript states for fresh start
                setFinalText("");
                setInterimText("");
                setEnhancedText("");
                setRecordingText("");
                setRecordingError(null);
                setAudioLevel(0);

                // Disconnect current session cleanly
                dictationService.disconnect();

                // Small delay to ensure clean disconnect
                await new Promise((resolve) => setTimeout(resolve, 200));

                // Restart recording with new language
                await startRecording();
              }
            }}
          />
        ) : (
          <RecordingBar
            recordingText={recordingText}
            onStop={handleStopRecording}
          />
        )
      ) : (
        <FloatingActionBar
          onActionClick={handleActionClick}
          editor={editor}
          canUndo={historyIndex > 0}
          canRedo={historyIndex < historyLength - 1}
          onUndo={() => {
            if (!editor || historyIndexRef.current <= 0) return;
            historyIndexRef.current -= 1;
            setHistoryIndex(historyIndexRef.current);
            console.log(`[UNDO] idx=${historyIndexRef.current}/${historyRef.current.length - 1}`);
            isUndoRedoRef.current = true;
            isInitializingRef.current = true;
            editor.commands.setContent(historyRef.current[historyIndexRef.current]);
            setTimeout(() => { isInitializingRef.current = false; }, 50);
            // Persist index to DB
            if (documentId) {
              console.log(`[UNDO] saving content_version_idx=${historyIndexRef.current} to DB`);
              DocumentService.updateDocument(documentId, { contentVersionIdx: historyIndexRef.current });
            }
          }}
          onRedo={() => {
            if (!editor || historyIndexRef.current >= historyRef.current.length - 1) return;
            historyIndexRef.current += 1;
            setHistoryIndex(historyIndexRef.current);
            console.log(`[REDO] idx=${historyIndexRef.current}/${historyRef.current.length - 1}`);
            isUndoRedoRef.current = true;
            isInitializingRef.current = true;
            editor.commands.setContent(historyRef.current[historyIndexRef.current]);
            setTimeout(() => { isInitializingRef.current = false; }, 50);
            // Persist index to DB
            if (documentId) {
              console.log(`[REDO] saving content_version_idx=${historyIndexRef.current} to DB`);
              DocumentService.updateDocument(documentId, { contentVersionIdx: historyIndexRef.current });
            }
          }}
        />
      )}

      {/* Upgrade Prompt */}
      <UpgradePrompt
        isVisible={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        onUpgrade={() => {
          setShowUpgradePrompt(false);
          setShowBillingModal(true);
        }}
      />

      {/* Sliding Sidebar */}
      <SlidingSidebar
        isOpen={isSidebarOpen}
        mode={sidebarMode}
        onClose={() => {
          setIsSidebarOpen(false);
          setSidebarMode(null);
        }}
        editorContent={editor?.getText() || ""}
        onRewrite={handleRewrite}
        onShowBilling={async () => {
          // Check if user is authenticated
          const {
            data: { user },
          } = await supabase.auth.getUser();
          if (!user) {
            // Show auth prompt for unauthenticated users
            setShowAuthPromptForMax(true);
          } else {
            // Show billing modal for authenticated users
            setShowBillingModal(true);
          }
        }}
      />

      {/* Billing Modal for MAX features */}
      <BillingModal
        isOpen={showBillingModal}
        onClose={() => setShowBillingModal(false)}
        onUpgradeSuccess={handleUpgradeSuccess}
      />

      {/* Auth Prompt Modal for MAX features (unauthenticated users) */}
      <AuthPromptModal
        isOpen={showAuthPromptForMax}
        onClose={() => setShowAuthPromptForMax(false)}
        onSuccess={() => {
          setShowAuthPromptForMax(false);
          // After successful auth, show billing modal
          setTimeout(() => {
            setShowBillingModal(true);
          }, 500);
        }}
        customMessage="Sign in to unlock MAX features with advanced AI models, unlimited rewrite options, and more!"
      />

      {/* Celebration Components - Rendered at root level using portal */}
      {(showConfetti || showSuccessMessage) &&
        ReactDOM.createPortal(
          <>
            {/* Confetti Celebration */}
            {showConfetti && (
              <div className="fixed inset-0 flex items-center justify-center z-[9999] pointer-events-none">
                <ConfettiExplosion
                  force={0.8}
                  duration={3000}
                  particleCount={250}
                  width={1600}
                  colors={[
                    "#0EA5E9",
                    "#38BDF8",
                    "#7DD3FC",
                    "#BAE6FD",
                    "#E0F2FE",
                  ]}
                />
              </div>
            )}

            {/* Success Message */}
            <AnimatePresence>
              {showSuccessMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 20, scale: 0.9 }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 30,
                  }}
                  className="fixed inset-0 flex items-start justify-center pt-20 z-[9998] pointer-events-none"
                >
                  <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 px-8 py-6 flex items-center space-x-4 pointer-events-auto">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 20,
                        delay: 0.2,
                      }}
                    >
                      <CheckCircle
                        className="w-8 h-8 text-green-500"
                        strokeWidth={1.5}
                      />
                    </motion.div>
                    <div>
                      <h3 className="text-lg font-light text-gray-900">
                        Welcome to {upgradedPlan}!
                      </h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Your account has been upgraded successfully
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>,
          document.body
        )}

      {/* Custom Editor Styles */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .ProseMirror {
            min-height: 400px;
          }

          .ProseMirror p.is-editor-empty:first-child::before {
            content: attr(data-placeholder);
            float: left;
            color: #9ca3af;
            pointer-events: none;
            height: 0;
          }

          .ProseMirror:focus {
            outline: none;
          }

          .has-focus {
            transition: all 0.2s ease;
          }

          /* Custom selection color */
          .ProseMirror ::selection {
            background-color: rgba(14, 165, 233, 0.15);
          }

          /* Smooth cursor */
          .ProseMirror {
            caret-color: #0ea5e9;
          }
        `,
        }}
      />
    </div>
  );
};

export default EditorPage;
